<template>
    <div
        class="dynamicMarket_cell"
        v-bind:class="{ _active: isActive, _suspended: selection.isSuspended || market.isSuspended }"
    >
        <div class="dynamicMarket_cell_name">
            {{ selection.name }}
        </div>
        <div
            class="dynamicMarket_cell_price"
            v-bind:class="priceClass"
            v-on:click="handleClick"
        >
            <template v-if="selection.isSuspended || market.isSuspended">
                <SvgIcon name="icon-system-odds-lock" />
            </template>
            <template v-else>
                <div class="dynamicMarket_cell_priceArea">
                    <span class="dynamicMarket_cell_priceValue">
                        {{ formatMinDecimalPlace(selection.price.value, 2) }}
                    </span>
                    <div class="priceStates" v-bind:class="priceChangeClass">
                        <div v-show="priceChangeClass" class="triangle" />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, toRefs } from 'vue';
    import SvgIcon from '@/components/common/SvgIcon.vue';
    import { usePriceChangeStatus } from '@/composable/usePriceChangeStatus';
    import { formatMinDecimalPlace } from '@/core/lib/numberFormat';
    import type { IMarket, IMarketSelection } from '@/interface/IMarket';
    import { PriceChangeStatus } from '@/interface/enum';

    const props = withDefaults(defineProps<{
        selection: IMarketSelection;
        market: IMarket;
        isActive?: boolean;
    }>(), {
        isActive: false,
    });

    const emit = defineEmits<{ (e: 'click'): void }>();

    const { selection, market } = toRefs(props);

    const price = computed(() => selection.value.price.value);
    const { priceChangeStatus } = usePriceChangeStatus(price);

    const isRise = computed(() => priceChangeStatus.value === PriceChangeStatus.Rise);
    const isDrop = computed(() => priceChangeStatus.value === PriceChangeStatus.Drop);

    const priceClass = computed(() => {
        if (selection.value.isSuspended || market.value.isSuspended) return 'selection-suspended';
        if (isRise.value) return 'price-rise';
        if (isDrop.value) return 'price-drop';
        return '';
    });

    const priceChangeClass = computed(() => {
        if (isRise.value) return 'priceStates-rise';
        if (isDrop.value) return 'priceStates-drop';
        return '';
    });

    function handleClick() {
        if (!selection.value.isSuspended && !market.value.isSuspended) emit('click');
    }
</script>

<style lang="scss" scoped>
    @import "@/components/oddsDisplay/moreMarket/marketType/DynamicMarket";
</style>
